import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Breadcrumb from '../components/Breadcrumb';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Plus, Trash2, Eye, MapPin, ArrowLeft, Loader2, Edit, BarChart3, Heart, TrendingUp, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stats Card Component
const StatCard = ({ icon: Icon, title, value, subtitle, color = 'emerald' }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    pink: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
  };

  return (
    <div className={`rounded-xl border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color].split(' ')[0]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-zinc-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-zinc-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

const MyListingsPage = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');

  useEffect(() => {
    if (user && token) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [listingsRes, analyticsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/my-listings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/my-listings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setListings(listingsRes.data);
      setAnalytics(analyticsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (listingId) => {
    try {
      await axios.delete(`${API}/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(listings.filter(l => l.id !== listingId));
    } catch (error) {
      console.error('Error deleting listing:', error);
    }
  };

  const getAnalyticsForListing = (listingId) => {
    return analytics.find(a => a.listing_id === listingId) || {
      total_views: 0,
      views_today: 0,
      views_this_week: 0
    };
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 text-center max-w-md">
          <h2 className="text-2xl font-bold text-white mb-4">{t('common.loginRequired')}</h2>
          <p className="text-zinc-400 mb-6">{t('common.pleaseLoginToCreate')}</p>
          <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700">
            {t('common.backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-800">
      {/* Breadcrumb */}
      <Breadcrumb items={[{ label: t('header.myListings'), href: null }]} />

      {/* Hero Section */}
      <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('analytics.myDashboard')}</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {t('header.myListings')}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/create-listing">
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              {t('listing.createListing')}
            </Button>
          </Link>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatCard
              icon={BarChart3}
              title={t('admin.totalListings')}
              value={summary.total_listings}
              color="blue"
            />
            <StatCard
              icon={Eye}
              title={t('analytics.totalViews')}
              value={summary.total_views}
              color="emerald"
            />
            <StatCard
              icon={TrendingUp}
              title={t('analytics.viewsToday')}
              value={summary.views_today}
              color="orange"
            />
            <StatCard
              icon={Calendar}
              title={t('analytics.thisWeek')}
              value={summary.views_this_week}
              color="purple"
            />
            <StatCard
              icon={Heart}
              title={t('favorites.totalFavorites')}
              value={summary.total_favorites}
              color="pink"
            />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('listings')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'listings'
                ? 'bg-emerald-600 text-white'
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            {t('header.myListings')}
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'analytics'
                ? 'bg-emerald-600 text-white'
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            {t('analytics.analytics')}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
          </div>
        ) : listings.length === 0 ? (
          <div className="bg-zinc-900 rounded-2xl p-12 border border-zinc-800 text-center">
            <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <Plus className="w-8 h-8 text-zinc-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-3">{t('listing.noListings')}</h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              {t('common.beFirstToCreate')}
            </p>
            <Link to="/create-listing">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                {t('listing.createListing')}
              </Button>
            </Link>
          </div>
        ) : activeTab === 'listings' ? (
          /* Listings Tab */
          <div className="grid gap-4">
            {listings.map((listing) => {
              const stats = getAnalyticsForListing(listing.id);
              return (
                <div
                  key={listing.id}
                  className="bg-zinc-900 rounded-xl p-4 md:p-6 border border-zinc-800 flex flex-col md:flex-row gap-4"
                >
                  <div className="w-full md:w-32 h-32 rounded-xl overflow-hidden bg-zinc-800 flex-shrink-0">
                    {listing.images?.[0] ? (
                      <img src={listing.images[0]} alt={listing.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-600 text-4xl font-bold">
                        {listing.name.charAt(0)}
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{listing.name}</h3>
                        <p className="text-zinc-400">{listing.title}</p>
                      </div>
                      {listing.status === 'pending' ? (
                        <Badge className="bg-amber-500/20 text-amber-400 border-0">
                          {t('listing.pendingApproval')}
                        </Badge>
                      ) : listing.status === 'approved' ? (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                          {t('listing.publishedStatus')}
                        </Badge>
                      ) : listing.status === 'rejected' ? (
                        <Badge className="bg-red-500/20 text-red-400 border-0">
                          {t('listing.rejectedStatus')}
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                          {t('listing.publishedStatus')}
                        </Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500 mb-3">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {listing.city}
                      </span>
                      <span>{listing.category}</span>
                    </div>

                    {/* Analytics Preview */}
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="flex items-center gap-1 text-emerald-400">
                        <Eye className="w-4 h-4" />
                        {stats.total_views} {t('analytics.totalViews').toLowerCase()}
                      </span>
                      <span className="text-zinc-500">
                        {t('analytics.viewsToday')}: {stats.views_today}
                      </span>
                      <span className="text-zinc-500">
                        {t('analytics.thisWeek')}: {stats.views_this_week}
                      </span>
                    </div>
                  </div>

                  <div className="flex md:flex-col gap-2 md:w-36">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      onClick={() => navigate(`/escort/${listing.slug || listing.id}`)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      {t('common.view')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-emerald-700 text-emerald-400 hover:bg-emerald-900/20"
                      onClick={() => navigate(`/edit-listing/${listing.id}`)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      {t('common.edit')}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="flex-1 border-red-900 text-red-400 hover:bg-red-900/20">
                          <Trash2 className="w-4 h-4 mr-1" />
                          {t('common.delete')}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">{t('listing.deleteListing')}</AlertDialogTitle>
                          <AlertDialogDescription className="text-zinc-400">
                            {t('listing.confirmDelete')} {t('common.thisActionCannotBeUndone')}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700">{t('common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(listing.id)}>
                            {t('common.delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Analytics Tab */
          <div className="space-y-6">
            {analytics.map((item) => (
              <div key={item.listing_id} className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-lg font-semibold text-white mb-4">{item.listing_name}</h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-zinc-800 rounded-lg p-4 text-center">
                    <p className="text-zinc-400 text-sm">{t('analytics.totalViews')}</p>
                    <p className="text-2xl font-bold text-white">{item.total_views}</p>
                  </div>
                  <div className="bg-zinc-800 rounded-lg p-4 text-center">
                    <p className="text-zinc-400 text-sm">{t('analytics.viewsToday')}</p>
                    <p className="text-2xl font-bold text-emerald-400">{item.views_today}</p>
                  </div>
                  <div className="bg-zinc-800 rounded-lg p-4 text-center">
                    <p className="text-zinc-400 text-sm">{t('analytics.thisWeek')}</p>
                    <p className="text-2xl font-bold text-blue-400">{item.views_this_week}</p>
                  </div>
                  <div className="bg-zinc-800 rounded-lg p-4 text-center">
                    <p className="text-zinc-400 text-sm">{t('analytics.thisMonth')}</p>
                    <p className="text-2xl font-bold text-purple-400">{item.views_this_month}</p>
                  </div>
                </div>

                {/* Simple Bar Chart */}
                {item.daily_views && item.daily_views.length > 0 && (
                  <div>
                    <p className="text-zinc-400 text-sm mb-3">{t('analytics.viewsLast30Days')}</p>
                    <div className="flex items-end gap-1 h-24">
                      {item.daily_views.slice(-30).map((day, idx) => {
                        const maxViews = Math.max(...item.daily_views.map(d => d.views), 1);
                        const height = (day.views / maxViews) * 100;
                        return (
                          <div
                            key={idx}
                            className="flex-1 bg-emerald-500 rounded-t hover:bg-emerald-400 transition-colors"
                            style={{ height: `${Math.max(height, 4)}%` }}
                            title={`${day.date}: ${day.views} ${t('analytics.totalViews').toLowerCase()}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {analytics.length === 0 && (
              <div className="text-center py-12 text-zinc-400">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t('analytics.noDataYet', 'Henüz analitik verisi yok. İlanlarınız görüntülendiğinde burada görünecek.')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyListingsPage;
